import { GoliathDocumentsEnumerationTypeStore } from './../../../../../enumeration-types/enumeration-type.store';
import { LocalizationModule, LocalizationService } from '@abp/ng.core';
import { AgGridAngular } from '@ag-grid-community/angular';
import { ColDef, GridOptions } from '@ag-grid-community/core';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Injector,
  Input,
  OnInit,
  output,
  ViewChild,
} from '@angular/core';
import { CustomAgGridOptions, FieldTypes, IEnumerationCellRendererParams } from '@digiwall/ag-grid';
import { PaymentMethodTypeStore } from '@digiwall/goliath-contacts';
import { CurrencyStore, GroupRowCellRendererComponent } from '@digiwall/goliath-core';
import { DocPaymentLookupDto } from '@digiwall/goliath-documents/proxy';

@Component({
  selector: 'document-payment-history-list',
  templateUrl: './payment-history-list.component.html',
  imports: [CommonModule, AgGridAngular, LocalizationModule],
})
export class PaymentHistoryListComponent implements OnInit {
  currencyStore = inject(CurrencyStore);
  docEnumerationTypeStore = inject(GoliathDocumentsEnumerationTypeStore);
  paymentMethodStore = inject(PaymentMethodTypeStore);

  @ViewChild('grid') grid: AgGridAngular;

  @Input() set payments(value: DocPaymentLookupDto[]) {
    this.buildPaymentLines(value.sort((a, b) => (a.documentDate < b.documentDate ? 1 : -1)));

    this.grid?.api?.setGridOption('rowData', this.paymentLines);
    this.grid?.api?.redrawRows();
  }

  cancelPayment = output<string>();

  protected paymentLines: any[] = [];

  gridOptions: GridOptions | undefined;
  defaultColDef = {
    floatingFilter: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
    suppressHeaderContextMenu: true,
  };

  columnDefs: ColDef[] = [
    {
      showRowGroup: 'id',
      cellRenderer: GroupRowCellRendererComponent,
      suppressHeaderMenuButton: true,
      pinned: 'left',
      suppressMovable: true,
      width: 50,
      resizable: false,
    },
    {
      field: 'documentDate',
      editable: true,
      headerName: this.localizationService.instant('::Date'),
      type: FieldTypes.Date,
    },
    {
      field: 'nature',
      editable: true,
      headerName: this.localizationService.instant('::Type'),
      type: FieldTypes.Enumeration,
      cellRendererParams: {
        entities: computed(() => [
          ...this.docEnumerationTypeStore.paymentNatures(),
          ...this.paymentMethodStore.entities(),
        ]),
      } as IEnumerationCellRendererParams,
    },
    {
      field: 'description',
      headerName: this.localizationService.instant('::Description'),
      type: FieldTypes.String,
    },
    {
      field: 'paymentMethod',
      headerName: this.localizationService.instant('GoliathCore::PaymentMethod'),
      type: FieldTypes.Enumeration,
      cellRendererParams: {
        entities: this.paymentMethodStore.entities,
      } as IEnumerationCellRendererParams,
    },
    {
      field: 'amount',
      headerName: this.localizationService.instant('Doc::Total'),
      type: FieldTypes.Currency,
    },
    {
      field: 'currency',
      headerName: this.localizationService.instant('::Currency'),
      type: FieldTypes.String,
    },
    {
      field: 'id',
      headerName: '',
      cellRendererParams: {
        onClick: (event: { id: string }) => this.cancelPayment.emit(event.id),
        title: 'Doc::CustomerInvoice:CancelPayment',
        icon: 'digi-out-cancel-payment',
        btnClass: 'btn btn-outline-danger',
      },
      cellClassRules: {
        'd-none': params => !params.value || !params.data.cancellable,
      },
      suppressColumnsToolPanel: true,
      type: FieldTypes.Button,
      pinned: 'right',
      width: 80,
      autoHeight: true,
      resizable: false,
    },
  ];

  constructor(
    private localizationService: LocalizationService,
    private injector: Injector,
  ) {
    this.currencyStore.ensureLoaded();
  }

  ngOnInit(): void {
    this.gridOptions = {
      context: this,
      rowModelType: 'clientSide',
      autoSizeStrategy: {
        type: 'fitGridWidth',
        defaultMinWidth: 40,
      },
      pagination: false,
      enableAdvancedFilter: false,
      rowHeight: 30,
      suppressContextMenu: false,
      rowSelection: {
        mode: 'multiRow',
        checkboxes: false,
        headerCheckbox: false,
      },
      getContextMenuItems: _ => [],
      getRowId: ({ data }: any) => {
        return data.id;
      },
      components: CustomAgGridOptions.Components,
      columnTypes: CustomAgGridOptions.ColumnTypeDefinitions(this.injector),
      rowData: [],

      treeData: true,
      getDataPath: this.getDataPath,
      groupDisplayType: 'custom',
      groupDefaultExpanded: 1,
      groupAllowUnbalanced: true,

      suppressDragLeaveHidesColumns: true,
    };
  }

  getDataPath = (line, paths: string[] = []) => {
    if (line == null) return paths;

    if (line.isPaymentLine && line.paymentId) {
      paths.push(line.paymentId);
    }

    paths.push(line.id);
    return paths;
  };

  getRowClass = params => {
    const line = params.node.data;

    if (line == null) return;

    let classes = [];
    if (!line.isPaymentLine) {
      classes.push('chapter-line');
    }
    return classes;
  };

  private buildPaymentLines(payments: DocPaymentLookupDto[]) {
    const paymentLines = [];

    payments.forEach(payment => {
      const mainPaymentLine = {
        id: payment.id,
        nature: payment.nature,
        description: payment.description,
        documentDate: payment.documentDate,
        amount: payment.docAmount?.totalAmount,
        currency: this.currencyStore.entities().find(x => x.id == payment.currencyId)?.code,
        cancellable: payment.cancellable,
      };

      paymentLines.push(mainPaymentLine);

      if (payment.docAmount && payment.docAmount.lines) {
        payment.docAmount.lines.forEach(line => {
          const paymentSubLine = {
            id: line.id,
            paymentId: payment.id,
            paymentMethod: line.paymentMethod,
            amount: line.amount,
            currency: this.currencyStore.entities().find(x => x.id == payment.currencyId)?.code,
            isPaymentLine: true,
            cancellable: false,
          };

          paymentLines.push(paymentSubLine);
        });
      }
    });

    this.paymentLines = paymentLines;
  }
}
